import { useTheme } from '@material-ui/core'
import { logAmplitudeEvent } from 'amplitude/amplitude'
import { Severities, variantIcon } from 'elements/notificationVariants'
import { useTranslate } from 'ra-core'
import React, { useEffect, useMemo } from 'react'
import type { OsTooltipPropsType } from './Tooltip'
import Tooltip from './Tooltip'

const TooltipTitleWrapper = ({
  children,
  trackSource,
}: {
  children: NonNullable<React.ReactNode>
  trackSource?: string
}) => {
  useEffect(() => {
    if (children == null || trackSource == null) {
      return
    }
    logAmplitudeEvent('generic_tooltip_interacted', {
      action: 'viewed',
      context: 'content',
      source: trackSource,
    })
  }, [])

  return <>{children}</>
}

type Sizes = 'small' | 'normal'

const InfoTooltip: React.FC<
  Omit<OsTooltipPropsType, 'children'> & {
    customIcon?: React.ReactNode
    trackSource?: string
    size?: Sizes
    severity?: Severities
    onOpen?: () => void
    onClose?: () => void
    style?: React.CSSProperties
    fixPosition?: boolean
  }
> = ({
  customIcon,
  title,
  disabled,
  placement,
  trackSource,
  size = 'normal',
  severity = 'info',
  fixPosition = false,
  ...rest
}) => {
  const theme = useTheme()
  const translate = useTranslate()
  const iconElement = useMemo(
    () =>
      React.createElement(variantIcon[severity], {
        style: {
          fontSize: size === 'small' ? '1.2em' : '1.5em',
          color: theme['alertIcon_' + severity],
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        },
      }),
    [severity, disabled, size]
  )
  return disabled ? (
    <>{iconElement}</>
  ) : (
    <Tooltip
      title={title && <TooltipTitleWrapper trackSource={trackSource}>{translate(title as string)}</TooltipTitleWrapper>}
      interactive={true}
      arrow
      placement={placement}
      {...(fixPosition && { PopperProps: { strategy: 'fixed' } })}
      {...rest}
    >
      {/* @ts-ignore */}
      {(customIcon as React.ReactNode) || iconElement}
    </Tooltip>
  )
}

export default InfoTooltip
